
import Vue from 'vue';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import { mapState, mapActions } from 'pinia';
import { mm2px } from '@/utils';
import { getScaledProductImageDPI } from '@/utils/areaHelper';
import { PREVIEW_IMAGE_BOX } from '@/web-components/import-wizard-v2/constants';
import { AnnotationShape } from '@/web-components/import-wizard-v2/typings/common';

type PreviewShape = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  pngFileUrl: string;
  rotation: number;
};

export default Vue.extend({
  name: 'ProductPreview',

  data() {
    return {
      PREVIEW_IMAGE_BOX,
      shapeUrlCache: {} as Record<string, string>,
      shapeBlobCache: {} as Record<string, Blob | null>,
    };
  },

  computed: {
    ...mapState(useProductStore, [
      'selectedPrintPosition',
      'selectedVariant',
      'activePosition',
    ]),
    ...mapState(useMirrorStore, ['currentMirrorSettings']),
    ...mapState(useShapeStore, ['positionShapes']),

    selectedVariantImageUrl(): string {
      if (this.activePosition && this.selectedVariant) {
        const variantImage = this.selectedVariant.images?.find(
          (img) => img.printPositionID === this.activePosition.id
        );
        return variantImage ? variantImage.imageWebUrl : '';
      }
      return '';
    },

    activePositionShapes(): PreviewShape[] {
      if (this.selectedPrintPosition && this.selectedPrintPosition.id) {
        const mirrorShapesFromPosition = (this as any).getActiveMirrorShapes();
        const mirrorShapesFromPositionInArea = [] as AnnotationShape[];

        let selectedShapes = this.positionShapes(
          this.selectedPrintPosition.id
        ).filter((x) => x.isInArea) as AnnotationShape[];

        mirrorShapesFromPosition.forEach((x: AnnotationShape) => {
          const originalShapeId = (x as any).originalShapeId;
          if (
            selectedShapes.findIndex((y) => y.id === originalShapeId) !== -1
          ) {
            mirrorShapesFromPositionInArea.push(x);
          }
        });

        selectedShapes = [...selectedShapes, ...mirrorShapesFromPositionInArea];
        const activeShapeIds = selectedShapes.map((shape) => shape.id);
        (this as any).cleanupUnusedShapeUrls(activeShapeIds);

        const mappedPositions = selectedShapes.map((x): PreviewShape | null => {
          if (this.activePosition.originalImageWidthPx) {
            const scaledDPI = getScaledProductImageDPI(
              this.activePosition.originalImageWidthPx,
              this.activePosition.originalImageHeightPx,
              this.activePosition.originalImageDpi,
              PREVIEW_IMAGE_BOX
            );

            return {
              id: x.id,
              x: mm2px(x.xOnProductMM, scaledDPI),
              y: mm2px(x.yOnProductMM, scaledDPI),
              width: mm2px(x.widthOnProductMM, scaledDPI),
              height: mm2px(x.heightOnProductMM, scaledDPI),
              pngFileUrl: (this as any).getShapeUrl(x),
              rotation: x.rotation,
            };
          }

          return null;
        });

        return mappedPositions.filter((shape): shape is PreviewShape => !!shape);
      }
      return [];
    },
  },
  methods: {
    ...mapActions(useMirrorStore, ['getActiveMirrorShapes']),

    getShapeUrl(shape: AnnotationShape): string {
      if (!shape.pngFile) {
        return '';
      }

      const nextBlob = shape.pngFile as Blob;
      const cachedBlob = this.shapeBlobCache[shape.id];
      const cachedUrl = this.shapeUrlCache[shape.id];

      if (cachedBlob === nextBlob && cachedUrl) {
        return cachedUrl;
      }

      if (cachedUrl) {
        URL.revokeObjectURL(cachedUrl);
      }

      const nextUrl = URL.createObjectURL(nextBlob);
      this.shapeBlobCache[shape.id] = nextBlob;
      this.shapeUrlCache[shape.id] = nextUrl;

      return nextUrl;
    },

    cleanupUnusedShapeUrls(activeShapeIds: string[]) {
      Object.keys(this.shapeUrlCache).forEach((shapeId) => {
        if (!activeShapeIds.includes(shapeId)) {
          URL.revokeObjectURL(this.shapeUrlCache[shapeId]);
          delete this.shapeUrlCache[shapeId];
          delete this.shapeBlobCache[shapeId];
        }
      });
    },

    clearAllShapeUrls() {
      Object.values(this.shapeUrlCache).forEach((url) => {
        URL.revokeObjectURL(url);
      });
      this.shapeUrlCache = {};
      this.shapeBlobCache = {};
    },
  },

  beforeDestroy() {
    (this as any).clearAllShapeUrls();
  },
});
